import { CONSTANTSType } from "../Modules/ModularConfigurator/ConfiguratorDisplay";

export type MainProduct = {
   id?: number;
   name: string;
   imageId?: number;
   image?: ImageType;
   title: string;
   initialConstants: CONSTANTSType;
   relativePos: number;
   hide: boolean;
   stages: Stage[];
   productCategoryId?: number | null;
   productCategory?: ProductCategory;
   initialPrice: number;
};

export type Stage = {
   id?: number;
   stageName: string;
   islandicName?: string;
   header?: string;
   imageTemplate: string[];
   showPrice: boolean;
   showImageGallery: boolean;
   type: StageTypes;
   relativePos: number;
   threshold: number;
   mainProductId: number;
   mainProduct?: MainProduct;
   options: Option[];
   optionPairId?: number;
   optionPair?: OptionPair;
};

export type Option = {
   id?: number;
   name: string;
   islandicName?: string;
   type: OptionTypes;
   optionId: string;
   defaultValue?: number;
   relativePos: number;
   stageId: number;
   stage: Stage;
   nodes: Node[];
   items: Item[];
   affectingItemManagers: ItemDependencyManager[];
   dependentItemManagers: ItemDependencyManager[];
};

export type Item = {
   id?: number;
   name: string;
   islandicName?: string;
   price: number;
   priceDepends?: boolean;
   relativePos: number;
   idName: string;
   optionId: number;
   option: Option;
   notice?: string;
   itemInNode: ImageInNode[];
   conditionEdges: Edge[];
   affectingdependencyManager: ItemDependencyManager[];
   dependentDependencyManager: ItemDependencyManager[];
   disabled?: boolean;
   learnMoreId?: number;
   learnMore?: LearnMore;
};

export type LearnMore = {
   id: number;
   content: string;
   imageId?: number;
   image?: ImageType;
   items: Item[];
   dependencies: LearnMoreDependency[];
   dependencyTemplates: LearnMoreDependencyTemplate[];
};

// ========== Dependency System ==========
export type ItemDependencyManager = {
   id?: number;
   dependentItemId: number;
   dependentItem?: Item;
   affectingItemId?: number;
   affectingItem?: Item;
   affectingOptionId?: number;
   affectingOption?: Option;
   dependentOptionId?: number;
   dependentOption?: Option;
   itemDependencyId?: number;
   itemDependency?: ItemDependency;
   priceDependencyId?: number;
   priceDependency?: PriceDependency;
   nameDependencyId?: number;
   nameDependency?: NameDependency;
   learnMoreDependencyId?: number;
   learnMoreDependency?: LearnMoreDependency;
};

export type ItemDependency = {
   id: number;
   dependencyManager?: ItemDependencyManager;
};

export type PriceDependency = {
   id: number;
   priceAdjustment: number;
   dependencyManager?: ItemDependencyManager;
};

export type NameDependency = {
   id: number;
   nameChange: string;
   islandicNameChange: string;
   dependencyManager?: ItemDependencyManager;
};

export type LearnMoreDependency = {
   id: number;
   learnMoreId?: number;
   learnMore?: LearnMore;
   dependencyManager?: ItemDependencyManager;
};

// ========== Dependency Templates ==========

export type DependencyTemplate = {
   id: number;
   affectingItemId?: number;
   affectingOptionId?: number;
   itemDependencyId?: number;
   itemDependency?: ItemDependencyTemplate;
   priceDependencyId?: number;
   priceDependency?: PriceDependencyTemplate;
   nameDependencyId?: number;
   nameDependency?: NameDependencyTemplate;
   learnMoreDependencyId?: number;
   learnMoreDependency?: LearnMoreDependencyTemplate;
};

export type NameDependencyTemplate = {
   id?: number;
   nameChange: string;
   islandicNameChange: string;
   manager?: DependencyTemplate;
};

export type LearnMoreDependencyTemplate = {
   id: number;
   learnMoreId?: number;
   learnMore?: LearnMore;
   manager?: DependencyTemplate;
};

export type ItemDependencyTemplate = {
   id?: number;
   manager?: DependencyTemplate;
};

export type PriceDependencyTemplate = {
   id?: number;
   priceAdjustment: number;
   manager?: DependencyTemplate;
};

// ========== Configuration Graph ==========
export type OptionPair = {
   id: number;
   name: string;
   mainImageId?: number;
   mainImage?: ImageType;
   nodes: Node[];
   edges: Edge[];
   stages: Stage[];
   graph?: Graph;
};

export type Node = {
   id: number;
   optionPairId: number;
   optionPair: OptionPair;
   nodeLabel?: string;
   optionId?: number;
   option?: Option;
   outgoingEdges: Edge[];
   incomingEdges: Edge[];
   imagesInNode: ImageInNode[];
   type: NodeType;
};

export type Edge = {
   id: number;
   optionPairId: number;
   optionPair: OptionPair;
   sourceNodeId: number;
   sourceNode: Node;
   targetNodeId: number;
   targetNode: Node;
   conditionItems: Item[];
   description?: string;
};

// ========== Media Management ==========
export type ImageInNode = {
   id?: number;
   nodeId: number;
   node: Node;
   imageId: number;
   image: ImageType;
   itemId?: number;
   item?: Item;
   zIndex: number;
   layerName?: string;
};
export type ImageType = {
   id: number;
   url: string;
   relativePos: number;
   title?: string;
   description?: string;
   altText?: string;
   width?: number;
   height?: number;
   size?: number;
   format?: string;
   createdAt?: Date;
   updatedAt?: Date;
   folderId?: number;
   folder?: { id: number; name: string };
   optionPair: OptionPair[];
   imageInNodes: ImageInNode[];
   tags: ImageTag[];
   mainProducts: MainProduct[];
   productCategory: ProductCategory[];
   learnMore: LearnMore[];
};
export type ImageTag = {
   id: number;
   name: string;
   images: ImageType[];
};
// ========== Enums ==========
export const OptionTypes = {
   RADIO: "radio",
   CHECKBOX: "checkbox",
   SUMMARY: "summary",
   OFFER: "offer",
   COLOR: "color",
   SELECT: "select",
   THRESHOLD: "threshold",
} as const;

export type OptionTypes = (typeof OptionTypes)[keyof typeof OptionTypes];

export const StageTypes = {
   ITEM: "item",
   SUMMARY: "summary",
   THRESHOLD: "threshold",
   OFFER: "offer",
} as const;

export type StageTypes = (typeof StageTypes)[keyof typeof StageTypes];

export const NodeType = {
   OPTION: "OPTION",
   CONNECTOR: "CONNECTOR",
   ROOT: "ROOT",
} as const;

export type NodeType = (typeof NodeType)[keyof typeof NodeType];

// ========== Form Types (Keep if still needed) ==========
export type Details = {
   name: string;
   lastName: string;
   email: string;
   phone: string;
   country: string;
   zipCode: string;
   notes: string;
};

export type SelectedItems = {
   selectedItem: string | string[];
   selected: boolean;
};

export type FormValues = {
   [key: string]: SelectedItems;
};
export type Graph = Record<
   number, // Node ID
   {
      nodeId: number; // Node ID
      nodeLabel: string; // Label for the node
      type?: NodeType; // Type of the node (e.g., "OPTION", "CONNECTOR", "ROOT")
      children: number[]; // Array of child node IDs
      imagesInNode: ImageInNode[]; // Images associated with the node
      items: Item[] | null; // Condition items from incoming edges (if any)
      outgoingEdges?: Edge[];
   }
>;

export type FolderType = {
   id: number;
   name: string;
   description?: string;
   parentId?: number;
   // Optional nested data:
   children?: FolderType[];
   images?: ImageType[];
   createdAt: string;
   updatedAt: string;
};

export type ProductCategory = {
   id: number;
   name: string;
   relativePos: number;
   imageId?: number;
   image?: ImageType;
   mainProducts?: MainProduct[];
};

export type MoneyFormatOptions = {
   currency?: "ISK" | "EUR" | "USD";
   vatLabelIncluded?: boolean;
};

export type Configuration = {
   productId: number;
   stageId: number;
   optionId: number;
   selectedItem?: string | string[];
   dependencies?: ItemDependencyManager[];
};
